import { useEffect, useState, useRef } from 'react';

import type { Provider } from '@atlaskit/collab-provider/provider';
import type { CollabEditProvider } from '@atlaskit/editor-common/collab';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { useEditorFlagsContext } from '@confluence/editor-flags/entry-points/EditorFlagsContext.ts';
import { ALLOWED_FLAG_MESSAGES } from '@confluence/editor-flags/entry-points/allowedFlagConstants.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';

type Payload = {
	version: number;
	status: 'attempt' | 'success' | 'failure';
};

/**
 * Listens to passed in collab provider's commit status events.
 * After a consecutive number of step rejections (determined by FF), the editor will show
 * an error flag and disable further editing.
 */
export const useCommitStatus = (
	collabEditProvider: (CollabEditProvider & Provider) | null,
	contentId: string,
	contentType: string,
): {
	hasExceededMaxStepFailures: boolean;
	stepFailureCount: number;
} => {
	const COLLAB_STEP_FAILURE_LIMIT = 150;
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { showEditorFlag } = useEditorFlagsContext();
	const isLivePage = useIsCurrentPageLive();

	// tracking the number of consecutive step failures
	const stepFailureCount = useRef<number>(0);
	const [hasExceededMaxStepFailures, setHasExceededMaxStepFailures] = useState<boolean>(false);
	const prevContentId = useRef<string>(contentId);

	// stepFailureCount is specific to the current contentId. If we're on a different contentId, reset stepFailureCount.
	useEffect(() => {
		if (contentId !== prevContentId.current) {
			stepFailureCount.current = 0;
			setHasExceededMaxStepFailures(false);
			prevContentId.current = contentId;
		}
	}, [contentId]);

	useEffect(() => {
		if (!collabEditProvider) {
			return;
		}

		const handleCommitStatus = ({ status, version }: Payload) => {
			if (!COLLAB_STEP_FAILURE_LIMIT) return; // only track failures/show flag for non-zero limit

			// `attempt` will either result in `success` or `failure`
			if (status === 'failure') {
				stepFailureCount.current++;
				if (!hasExceededMaxStepFailures && stepFailureCount.current > COLLAB_STEP_FAILURE_LIMIT) {
					setHasExceededMaxStepFailures(true);
					showEditorFlag({
						errorName: ALLOWED_FLAG_MESSAGES.COLLAB_STATUS_ERROR,
					});
					// reset consecutive failure count
					stepFailureCount.current = 0;
					createAnalyticsEvent({
						type: 'sendOperationalEvent',
						data: {
							action: 'stepFailureLimitExceeded',
							actionSubject: 'nativeEditor',
							source: 'confluenceFrontend',
							objectId: contentId,
							objectType: contentType,
							attributes: {
								version,
							},
						},
					}).fire();
				}
			} else if (status === 'success') {
				// reset consecutive failure count
				stepFailureCount.current = 0;
				setHasExceededMaxStepFailures(false);
			}
		};

		collabEditProvider.on('commit-status', handleCommitStatus);
		return () => {
			collabEditProvider.off('commit-status', handleCommitStatus);
		};
	}, [
		collabEditProvider,
		COLLAB_STEP_FAILURE_LIMIT,
		contentId,
		contentType,
		createAnalyticsEvent,
		showEditorFlag,
		isLivePage,
		hasExceededMaxStepFailures,
	]);

	return {
		hasExceededMaxStepFailures,
		stepFailureCount: stepFailureCount.current,
	};
};
